import React from "react";
import { Heart, MapPin, Phone, BedDouble, BadgeCheck, Star, Navigation, Clock, AlertTriangle } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { categoryLabel } from "@/lib/categories";

function agoLabel(days) {
  if (days == null) return "";
  if (days < 1) return "today";
  if (days < 2) return "yesterday";
  return `${Math.round(days)} days ago`;
}

// Shows how recently a facility's live bed count was confirmed. Only meaningful for
// paid tiers with a tracked availability; hidden for free tier / never-updated.
export function FreshnessBadge({ facility, className = "" }) {
  if (!facility || facility.tier === "free" || !facility.tier) return null;
  const tier = facility.staleness_tier;
  const days = facility.days_since_beds_update;
  if (!tier || tier === "never") return null;
  const styles = {
    fresh: "bg-[#E3F3ED] text-[#146648]",
    aging: "bg-[#FDF3E2] text-[#8A5A14]",
    stale: "bg-[#FBE6E6] text-[#C0392B]",
  };
  const Icon = tier === "stale" ? AlertTriangle : Clock;
  const text = tier === "stale" ? `Availability may be outdated · ${agoLabel(days)}` : `Updated ${agoLabel(days)}`;
  return (
    <span data-testid="freshness-badge" data-tier={tier}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${styles[tier]} ${className}`}>
      <Icon className="h-3 w-3" /> {text}
    </span>
  );
}

export function BedBadge({ beds, tier }) {
  // Live bed counts are a paid feature (Basic & Premium only).
  if (tier === "free" || !tier) {
    return <span data-testid="bed-badge" className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium bg-[#EFEEE8] text-[#5C6660]">Contact for availability</span>;
  }
  if (beds === null || beds === undefined) {
    return <span data-testid="bed-badge" className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium bg-[#EFEEE8] text-[#5C6660]">Availability unknown</span>;
  }
  if (beds <= 0) {
    return <span data-testid="bed-badge" className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium bg-[#FBE6E6] text-[#C0392B]">No beds open</span>;
  }
  return <span data-testid="bed-badge" className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-[#E3F3ED] text-[#1A365D]"><BedDouble className="h-3.5 w-3.5" />{beds} bed{beds > 1 ? "s" : ""} available</span>;
}

export function FacilityCard({ facility, active, isFavorite, onSelect, onToggleFavorite, onHover, claimMode, onClaim }) {
  const { settings } = useSettings();
  const s = settings || {};
  // Only paid/premium facilities with a REAL uploaded photo show an image.
  // Free tier and premium-without-photos render as a clean, text-only card.
  const isPremium = facility.tier === "premium";
  const img = isPremium ? facility.photos?.[0] : null;
  const hasPhoto = Boolean(img);

  const favoriteBtn = (
    <button
      data-testid="favorite-btn"
      aria-label={isFavorite ? "Remove from saved facilities" : "Save this facility"}
      aria-pressed={isFavorite}
      onClick={(e) => { e.stopPropagation(); onToggleFavorite(facility); }}
      className={hasPhoto
        ? "absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:scale-110 transition-transform"
        : "shrink-0 h-9 w-9 -mt-1 -mr-1 rounded-full bg-[#F5F4EF] flex items-center justify-center hover:scale-110 transition-transform"}
    >
      <Heart className={`h-4.5 w-4.5 ${isFavorite ? "fill-[#0077B6] text-[#0077B6]" : "text-[#4A534E]"}`} />
    </button>
  );

  return (
    <div
      data-testid="facility-card"
      data-has-photo={hasPhoto}
      onClick={() => onSelect(facility)}
      onMouseEnter={() => onHover?.(facility.facility_id)}
      onMouseLeave={() => onHover?.(null)}
      className={`group flex flex-col h-full cursor-pointer bg-white border rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
        isPremium ? "border-[#0077B6] ring-1 ring-[#0077B6]/30" : active ? "border-[#1A365D] shadow-lg" : "border-[#E8E6DF] hover:border-[#1A365D]/30"
      }`}
    >
      {hasPhoto && (
        <div className="relative h-40 overflow-hidden">
          <img src={img} alt={facility.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          {!claimMode && favoriteBtn}
          <div className="absolute top-3 left-3 flex gap-2">
            {isPremium && (
              <span data-testid="featured-badge" className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold bg-[#0077B6] text-white">
                <Star className="h-3.5 w-3.5 fill-white" /> Featured
              </span>
            )}
            {facility.claimed && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium bg-white/90 text-[#1A365D]">
                <BadgeCheck className="h-3.5 w-3.5" /> Verified
              </span>
            )}
          </div>
        </div>
      )}
      <div className="p-4 md:p-5 flex flex-col flex-1">
        {!hasPhoto && (isPremium || facility.claimed) && (
          <div className="flex flex-wrap gap-2 mb-3">
            {isPremium && (
              <span data-testid="featured-badge" className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold bg-[#0077B6] text-white">
                <Star className="h-3.5 w-3.5 fill-white" /> Featured
              </span>
            )}
            {facility.claimed && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium bg-[#E3F3ED] text-[#1A365D]">
                <BadgeCheck className="h-3.5 w-3.5" /> Verified
              </span>
            )}
          </div>
        )}
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="font-heading text-base md:text-lg font-medium text-[#1C211F] leading-snug">{facility.name}</h3>
          {!hasPhoto && !claimMode && favoriteBtn}
        </div>
        <p className="text-xs uppercase tracking-wide text-[#5C6660] font-medium mb-3">{categoryLabel(s, facility.category, facility.facility_type)}{s.card_show_type_detail !== false && facility.facility_type_detail ? ` · ${facility.facility_type_detail}` : ""}</p>
        {s.card_show_location !== false && (
          <div className="flex items-center gap-1.5 text-sm text-[#4A534E] mb-1">
            <MapPin className="h-4 w-4 shrink-0 text-[#5C6660]" /> <span className="truncate">{facility.city}, {facility.state} · {facility.county} County</span>
          </div>
        )}
        <div className="flex items-center justify-between text-sm text-[#4A534E] mb-3">
          {s.card_show_phone !== false
            ? <span className="flex items-center gap-1.5"><Phone className="h-4 w-4 shrink-0 text-[#5C6660]" /> {facility.telephone}</span>
            : <span />}
          {facility.distance_miles != null && (
            <span className="flex items-center gap-1 text-xs text-[#0077B6] font-mono-data"><Navigation className="h-3.5 w-3.5" />{facility.distance_miles} mi</span>
          )}
        </div>
        <div className="flex items-center justify-between mt-auto">
          {s.card_show_availability !== false ? <BedBadge beds={facility.available_beds} tier={facility.tier} /> : <span />}
          {s.card_show_licensed_beds !== false && (
            <span className="text-xs text-[#5C6660] font-mono-data">{facility.licensed_beds_total} licensed</span>
          )}
        </div>
        {s.card_show_availability !== false && !claimMode && (
          <div className="mt-2"><FreshnessBadge facility={facility} /></div>
        )}
        {claimMode && (
          <div className="mt-3 pt-3 border-t border-[#E8E6DF]">
            {facility.claimed || facility.claim_status === "approved" ? (
              <span data-testid="claimed-badge" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium bg-[#E3F3ED] text-[#146648]">
                <BadgeCheck className="h-4 w-4" /> Claimed
              </span>
            ) : facility.claim_status === "pending" ? (
              <button disabled data-testid="claim-pending-badge" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium bg-[#FDF3E2] text-[#8A5A14] cursor-not-allowed">
                <Clock className="h-4 w-4" /> Claim pending review
              </button>
            ) : (
              <button data-testid="claim-listing-btn" onClick={(e) => { e.stopPropagation(); onClaim?.(facility); }}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-semibold bg-[#1A365D] hover:bg-[#12283F] text-white transition-colors">
                <BadgeCheck className="h-4 w-4" /> Claim this listing
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
